import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Target, Award, Users, CheckCircle, TrendingUp, Clock,
  BarChart3, Video, FileText, Headphones, Calendar, ArrowRight, Star
} from 'lucide-react';
import Footer from '../components/Footer';

const ExamPrep = () => {
  const navigate = useNavigate();
  const [selectedExam, setSelectedExam] = useState('all');

  const stats = [
    { value: '5,000+', label: 'Students Prepared', icon: Users },
    { value: '94%', label: 'Success Rate', icon: Award },
    { value: '150+', label: 'Practice Tests', icon: Target },
    { value: '+25%', label: 'Avg Score Increase', icon: TrendingUp }
  ];

  const exams = [
    {
      id: 'ielts',
      name: 'IELTS',
      fullName: 'International English Language Testing System',
      image: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=600&h=400&fit=crop',
      duration: '8-12 weeks',
      targetScore: 'Band 6.5+',
      tests: '30+ Practice Tests',
      students: '1,200+'
    },
    {
      id: 'toefl',
      name: 'TOEFL',
      fullName: 'Test of English as a Foreign Language',
      image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&h=400&fit=crop',
      duration: '8-12 weeks',
      targetScore: '90+ Score',
      tests: '25+ Practice Tests',
      students: '800+'
    },
    {
      id: 'gre',
      name: 'GRE',
      fullName: 'Graduate Record Examination',
      image: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=600&h=400&fit=crop',
      duration: '10-16 weeks',
      targetScore: '320+ Score',
      tests: '20+ Practice Tests',
      students: '600+'
    },
    {
      id: 'gmat',
      name: 'GMAT',
      fullName: 'Graduate Management Admission Test',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop',
      duration: '10-16 weeks',
      targetScore: '700+ Score',
      tests: '15+ Practice Tests',
      students: '500+'
    },
    {
      id: 'sat',
      name: 'SAT',
      fullName: 'Scholastic Assessment Test',
      image: 'https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=600&h=400&fit=crop',
      duration: '8-12 weeks',
      targetScore: '1400+ Score',
      tests: '20+ Practice Tests',
      students: '900+'
    },
    {
      id: 'act',
      name: 'ACT',
      fullName: 'American College Testing',
      image: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=600&h=400&fit=crop',
      duration: '8-12 weeks',
      targetScore: '30+ Score',
      tests: '15+ Practice Tests',
      students: '400+'
    }
  ];

  const features = [
    {
      icon: Video,
      title: 'Live Classes',
      description: 'Interactive sessions with expert instructors and real-time doubt clearing'
    },
    {
      icon: FileText,
      title: 'Study Materials',
      description: 'Comprehensive guides, notes, and practice questions for all sections'
    },
    {
      icon: BarChart3,
      title: 'Progress Tracking',
      description: 'Detailed analytics on your performance and improvement areas'
    },
    {
      icon: Headphones,
      title: 'Personal Coaching',
      description: 'One-on-one sessions tailored to your specific needs and goals'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      exam: 'IELTS',
      score: 'Band 8.0',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face',
      content: 'The speaking practice sessions were invaluable. I went from 6.5 to 8.0 in just 8 weeks.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      exam: 'GRE',
      score: '330/340',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      content: 'The quant drills and vocab builder helped me achieve my dream score. Highly recommended.',
      rating: 5
    },
    {
      name: 'Priya Patel',
      exam: 'TOEFL',
      score: '118/120',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      content: 'Excellent study materials and supportive instructors. Got into my top choice university.',
      rating: 5
    }
  ];

  const filteredExams = selectedExam === 'all' ? exams : exams.filter(e => e.id === selectedExam);

  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white pt-20 pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Ace Your Exams with
              <span className="text-yellow-500"> Expert Preparation</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-10">
              Comprehensive test preparation for IELTS, TOEFL, GRE, GMAT, SAT, and ACT. Expert instructors, proven strategies, and personalized coaching to help you achieve your target score.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={() => navigate('/signup')}
                className="inline-flex items-center justify-center px-8 py-4 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button
                onClick={() => document.getElementById('courses-section').scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-lg border-2 border-gray-200 transition-all duration-200"
              >
                Browse Courses
              </button>
            </div>

            <div className="flex items-center justify-center space-x-8">
              <div className="flex items-center">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <span className="ml-2 text-sm text-gray-600">4.9/5 rating</span>
              </div>
              <div className="text-sm text-gray-600">5,000+ students prepared</div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="h-10 w-10 text-yellow-500 mx-auto mb-3" />
                  <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-gray-400">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive tools and resources for effective exam preparation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group p-8 rounded-2xl border border-gray-200 hover:border-yellow-500 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-yellow-500 transition-colors duration-300">
                    <Icon className="h-7 w-7 text-yellow-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses-section" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your Exam
            </h2>
            <p className="text-xl text-gray-600">
              Expert preparation courses for all major standardized tests
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => setSelectedExam('all')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                selectedExam === 'all'
                  ? 'bg-yellow-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
              }`}
            >
              All Exams
            </button>
            {exams.map((exam) => (
              <button
                key={exam.id}
                onClick={() => setSelectedExam(exam.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  selectedExam === exam.id
                    ? 'bg-yellow-500 text-white shadow-lg'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
                }`}
              >
                {exam.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredExams.map((exam) => (
              <div
                key={exam.id}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={exam.image}
                    alt={exam.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 px-3 py-1 bg-white rounded-full text-sm font-semibold text-gray-900">
                    {exam.students} students
                  </div>
                </div>

                <div className="p-8">
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">{exam.name}</h3>
                  <p className="text-sm text-gray-600 mb-6">{exam.fullName}</p>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Duration</span>
                      <span className="font-semibold text-gray-900">{exam.duration}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Target Score</span>
                      <span className="font-semibold text-gray-900">{exam.targetScore}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Practice Tests</span>
                      <span className="font-semibold text-gray-900">{exam.tests}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/signup')}
                    className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-all duration-200"
                  >
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              Real results from students who achieved their target scores
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                <div className="flex items-center mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">{testimonial.content}</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.exam} - {testimonial.score}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Achieve Your Target Score?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Join thousands of successful students who achieved their dreams with our expert guidance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="inline-flex items-center justify-center px-8 py-4 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Start Your Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <button
              onClick={() => navigate('/services-overview')}
              className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-100 text-gray-900 font-semibold rounded-lg transition-all duration-200"
            >
              View All Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ExamPrep;
